import { useCallback, useEffect, useRef, useState } from 'react';
import {
  findLanguage,
  isVasteKamer,
  type FeedbackKind,
  type Gender,
  type Thumb,
} from '@tolk/shared';
import type { ReactNode } from 'react';
import { useRoomSocket, type BinnengekomenAudio } from '../hooks/useRoomSocket';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import Instellingen from '../components/Instellingen';
import { useSpreken, type SpreekProbleem } from '../hooks/useSpreken';
import { useSchermWakker } from '../hooks/useSchermWakker';
import Bevestiging from '../components/Bevestiging';
import Woordenlijst from '../components/Woordenlijst';
import { teksten, type Teksten } from '../teksten';
import Kader from '../components/Kader';
import Kostenbalk, { Budgetwaarschuwing, useKostenStand } from '../components/Kostenbalk';
import MeetKnop from '../components/MeetKnop';
import MeetBlok from '../components/MeetBlok';
import PauzeInstellen from '../components/PauzeInstellen';
import { isMijnKamer } from '../lib/mijnKamers';
import Bericht from '../components/Bericht';
import PlekBezet from '../components/PlekBezet';
import Meetvak from '../components/Meetvak';
import { kort, meten, noteer } from '../lib/meetlog';
import { verslagPad, type KamerPubliek } from '../lib/api';

/**
 * In de kamer. Stap 6 laat alleen zien DAT je verbonden bent en of de ander er
 * is. Praten, vertalen en spraak komen in stap 7 tot en met 9.
 */

/**
 * Zo lang blijft de wachtaanduiding bij de afspeelknop staan als de server
 * niets terugstuurt.
 *
 * Ruim, want opnieuw laten inspreken van een lange zin duurt seconden. Dit is
 * alleen een vangnet tegen een knop die voor eeuwig op "bezig" blijft staan;
 * het normale geval ruimt zichzelf op zodra de audio binnenkomt.
 */
const WACHT_OP_AUDIO_MS = 30_000;

/** De microfoonmelding in de taal die de gebruiker koos. */
function spreekMelding(probleem: SpreekProbleem, t: Teksten): string {
  switch (probleem) {
    case 'geen_opname':
      return t.geenOpnameMogelijk;
    case 'gedempt':
      return t.foutMicroGedempt;
    case 'te_lang':
      return t.foutOpnameTeLang;
    case 'niets_opgenomen':
      return t.foutNietsOpgenomen;
    case 'niets_gehoord':
      return t.foutNietsGehoord;
    case 'geweigerd':
      return t.foutMicroGeweigerd;
    case 'geen_microfoon':
      return t.foutGeenMicro;
    case 'in_gebruik':
      return t.foutMicroInGebruik;
    case 'geen_https':
      return t.foutGeenHttps;
    default:
      return t.foutMicroOnbekend;
  }
}

function foutmelding(code: string, t: Teksten): string {
  const per: Record<string, string> = {
    room_full: t.foutKamerVol,
    room_expired: t.foutKamerVerlopen,
    room_not_found: t.foutKamerOnbekend,
    pin_required: t.foutPinNodig,
    pin_wrong: t.foutPinFout,
    protocol_mismatch: t.foutProtocol,
    bad_language: t.foutTaal,
    overgenomen: t.overgenomenUitleg,
  };
  return per[code] ?? t.foutKamerVerlopen;
}

export default function RoomPage({
  kamer,
  pin,
  lang,
  gender,
  speler,
  terug,
}: {
  kamer: KamerPubliek;
  pin: string | null;
  lang: string;
  gender: Gender;
  speler: ReturnType<typeof useAudioPlayer>;
  terug: () => void;
}) {

  // Vertaling meteen afspelen, of alleen op de knop. Bewaard, want dit is een
  // voorkeur en geen instelling per gesprek.
  const [gelijkAfspelen, setGelijkAfspelen] = useState(() => {
    try {
      return localStorage.getItem('tolk.gelijkAfspelen') !== 'nee';
    } catch {
      return true;
    }
  });

  /**
   * Welke berichten al vanzelf hebben geklonken.
   *
   * Zonder deze lijst speelt hetzelfde bericht opnieuw af zodra het nog een
   * keer binnenkomt - bijvoorbeeld na een herverbinding. In een ref en niet in
   * state: het mag geen hertekening veroorzaken en moet meteen gelden.
   */
  const alGeklonken = useRef(new Set<string>());

  /**
   * Wat er zichtbaar is: inspreken, typen, of allebei.
   *
   * In de trein kun je niet praten en wil je alleen typen. Thuis is typen juist
   * in de weg. Allebei tegelijk tonen kost een kwart van het scherm aan iets
   * dat je op dat moment niet gebruikt.
   *
   * Er moet er ALTIJD een aan blijven staan - anders kun je niets meer zeggen
   * en is er geen knop om dat terug te draaien.
   */
  const [spreken, setSpreken] = useState(() => leesStand('tolk.spreken', true));
  const [typen, setTypen] = useState(() => leesStand('tolk.typen', true));

  /**
   * Hoe groot de tekst staat, 0 tot en met 4.
   *
   * Niet iedereen leest even makkelijk, en een tolkgesprek is juist het moment
   * waarop je de tekst goed wilt kunnen zien. Bewaard, want het is een
   * eigenschap van de LEZER en niet van het gesprek.
   */
  const [grootte, setGrootte] = useState(() => {
    try {
      const ruw = localStorage.getItem('tolk.grootte');
      if (ruw === null) return 1;
      const n = Number(ruw);
      return Number.isInteger(n) && n >= 0 && n <= 4 ? n : 1;
    } catch {
      return 1;
    }
  });

  function zetGrootte(n: number): void {
    const begrensd = Math.max(0, Math.min(4, n));
    setGrootte(begrensd);
    try {
      localStorage.setItem('tolk.grootte', String(begrensd));
    } catch {
      // Geblokkeerde opslag. Dan geldt hij alleen dit gesprek.
    }
  }

  /** Berichten waarvan de audio expres is opgevraagd. Die mogen altijd klinken. */
  const gevraagdRef = useRef(new Set<string>());

  /**
   * Het bericht waarvan we op geluid staan te wachten, of null.
   *
   * WAAROM DIT ER MOET ZIJN. Staat de zin nog in het geheugen van de server,
   * dan komt hij meteen terug en flikkert dit hooguit even. Is hij vervallen -
   * na tien minuten, of na een herstart van de server - dan laat de server hem
   * opnieuw inspreken, en dat duurt een seconde of wat. Zonder aanduiding is
   * dat niet te onderscheiden van een knop die stuk is, en dat is precies de
   * klacht waarmee deze reparatie begon.
   */
  const [wachtOpAudio, setWachtOpAudio] = useState<string | null>(null);

  // In een ref, want de opAudio-functie leest hem uit een oude render.
  const gelijkAfspelenRef = useRef(gelijkAfspelen);
  gelijkAfspelenRef.current = gelijkAfspelen;

  /**
   * BEURTBEHEER, DE HELFT DIE HIER THUISHOORT: EEN GELUIDSBRON TEGELIJK.
   *
   * Staat je eigen microfoon open, dan mag de app niet gaan praten. Deed hij
   * dat wel, dan hoorde je microfoon de vertaling van de ander en gebeurden er
   * twee dingen tegelijk: de opname stopte nooit meer, en de opname die je
   * verstuurde bevatte de stem van de app. De server schreef dat vervolgens
   * netjes uit als iets wat jij gezegd zou hebben.
   *
   * Wat binnenkomt gaat daarom in de wacht en klinkt zodra je klaar bent. Niet
   * weggooien: de vertaling van de ander is het enige dat je begrijpt.
   */
  const luistertRef = useRef(false);
  const uitgesteldRef = useRef<BinnengekomenAudio[]>([]);

  // Alleen voor het meetlog: wanneer de kamer openging.
  useEffect(() => {
    noteer('kamer: open');
  }, []);

  /**
   * DE MICROFOON MOET LOS VOORDAT ER IETS KLINKT. Gemeten op 11 september
   * 2026, vier rondes op de iPhone: zolang de app een levend microfoonspoor
   * vasthoudt, stuurt iOS al het geluid naar het oortje - de vertaling klinkt
   * dan alsof iemand belt. Stoppen en meteen afspelen geeft de luidspreker
   * terug. Uitzetten (enabled = false) doet dat niet, en navigator.audioSession
   * helpt ook niet: 'playback' weigert de microfoon en 'play-and-record' geeft
   * het oortje.
   *
   * Een ref, want de spreker wordt pas verderop gemaakt en deze functies
   * bestaan al. Gezet in een effect en niet tijdens het renderen.
   */
  const laatMicrofoonLosRef = useRef<() => void>(() => {});

  const speelNu = useCallback(
    (audio: BinnengekomenAudio) => {
      if (audio.deel > 0) speler.meldVervolg();
      noteer(`audio binnen ${kort(audio.messageId)} deel ${audio.deel}: nu spelen`);
      laatMicrofoonLosRef.current();
      void speler.speel(audio.base64, audio.sampleRate, audio.messageId).catch(() => {
        // Een fragment dat niet speelt mag het gesprek niet stoppen.
      });
    },
    [speler],
  );

  /** Aangeroepen door het gespreksdeel zodra de microfoon open- of dichtgaat. */
  const meldLuistert = useCallback(
    (aan: boolean) => {
      luistertRef.current = aan;
      if (aan) return;
      // De volgorde blijft zoals hij binnenkwam: stukken van een zin horen op
      // de goede plek terug.
      const wachtenden = uitgesteldRef.current;
      uitgesteldRef.current = [];
      for (const audio of wachtenden) speelNu(audio);
    },
    [speelNu],
  );

  const verbinding = useRoomSocket({
    kamerId: kamer.id,
    pin,
    lang,
    gender,
    actief: true,
    // Staat meteen afspelen uit, dan hoeft de server ook geen spraak te maken:
    // die hoort niemand. De ▶ maakt hem alsnog, op dat moment. Taak 2 van de
    // takenlijst van 23 september 2026; twee vinkjes zijn bewust EEN geworden.
    autoSpraak: gelijkAfspelen,
    opAudio: (audio) => {
      // Het wachten is voorbij zodra er iets voor DIT bericht binnenkomt.
      // Vergelijken in de bijwerkfunctie zelf, niet met de waarde uit deze
      // render: die is hier oud.
      setWachtOpAudio((huidig) => (huidig === audio.messageId ? null : huidig));

      // Een VERVOLGSTUK van dezelfde zin hoort er altijd achteraan, ook al is
      // het bericht al als "geklonken" gemarkeerd.
      if (audio.deel > 0) {
        if (luistertRef.current) uitgesteldRef.current.push(audio);
        else speelNu(audio);
        return;
      }

      // Op verzoek altijd; vanzelf alleen als dat aanstaat en dit bericht nog
      // niet eerder heeft geklonken.
      const gevraagd = gevraagdRef.current.delete(audio.messageId);
      if (!gevraagd) {
        if (!gelijkAfspelenRef.current) return;
        if (alGeklonken.current.has(audio.messageId)) return;
        alGeklonken.current.add(audio.messageId);
      }
      // Wacht tot de microfoon dicht is. Zie luistertRef hierboven.
      if (luistertRef.current) {
        noteer(`audio binnen ${kort(audio.messageId)}: uitgesteld, microfoon open`);
        uitgesteldRef.current.push(audio);
        return;
      }
      speelNu(audio);
    },
    opBronAudio: (audio) => {
      // Via de speler, niet los: anders klinkt de originele stem over de
      // vertaling heen en houdt niets dat tegen.
      laatMicrofoonLosRef.current();
      void speler.speelOpname(audio.base64, audio.mimeType, audio.messageId).catch(() => {
        // Stil laten. De vertaling is er wel.
      });
    },
  });

  /**
   * DE WACHTAANDUIDING MAG NOOIT BLIJVEN HANGEN.
   *
   * Er komt geen antwoord als de server niets kan maken: een bericht dat hij
   * niet kent, een vertaling die nooit afkwam, of een rem die dichtstaat. Bij
   * een foutcode gaat de aanduiding meteen uit - die melding staat al in beeld
   * en legt het uit. Anders vangt de klok hem op.
   */
  useEffect(() => {
    if (verbinding.foutcode !== null) setWachtOpAudio(null);
  }, [verbinding.foutcode]);

  useEffect(() => {
    if (wachtOpAudio === null) return;
    const klaar = setTimeout(() => setWachtOpAudio(null), WACHT_OP_AUDIO_MS);
    return () => clearTimeout(klaar);
  }, [wachtOpAudio]);

  /**
   * De kostenstand, alleen voor de maker (zie lib/mijnKamers.ts). Hier en niet
   * in de balk, omdat hij ook bovenaan de waarschuwing voedt.
   */
  const mijnKamer = isMijnKamer(kamer.id);
  const kosten = useKostenStand(kamer.id, verbinding.berichten.length, mijnKamer);
  const verversKosten = kosten.ververs;

  // Een geweigerde zin voegt geen bericht toe, dus de stand ververst dan niet
  // vanzelf. Juist dan wil de maker zien dat het plafond bereikt is.
  useEffect(() => {
    if (verbinding.foutcode === 'budget_room') verversKosten();
  }, [verbinding.foutcode, verversKosten]);

  /**
   * MIJN taal volgt de LEVENDE instelling, niet de keuze bij het binnenkomen.
   *
   * Wie zijn taal midden in het gesprek omzet, wil ook de knoppen en meldingen
   * in die taal - anders zit je Duits te praten met een Nederlands scherm. Voor
   * het joinen is er nog geen plek bekend; dan geldt wat je bij de deur koos.
   */
  const opPlekA = verbinding.slot !== 'b';
  const instellingen = verbinding.settings;
  const mijnTaalCode =
    instellingen === null ? lang : opPlekA ? instellingen.langA : instellingen.langB;
  const anderTaalCode =
    instellingen === null
      ? lang === kamer.langA
        ? kamer.langB
        : kamer.langA
      : opPlekA
        ? instellingen.langB
        : instellingen.langA;
  const t = teksten(mijnTaalCode);

  // De LEVENDE stand zodra die er is: de beheerder kan beide tijdens het
  // gesprek wijzigen. null bij meetLink betekent "weggehaald", niet "onbekend" -
  // daarom geen ?? op meetLink zelf.
  const meetLink = instellingen !== null ? instellingen.meetLink : kamer.meetLink;
  // Ook levend: allebei mogen hem tijdens het gesprek omzetten.
  const alleenChat = instellingen?.alleenChat ?? kamer.alleenChat;
  // Ook levend: de beheerder zet hem tijdens het gesprek, en dan moet de
  // stiltemeter van de gast meteen mee.
  const pauzeMs = instellingen?.pauzeMs ?? kamer.pauzeMs;
  const verlooptOp = instellingen?.expiresAt ?? kamer.expiresAt;

  if (verbinding.stand === 'geweigerd' || verbinding.stand === 'gesloten') {
    const code = verbinding.foutcode ?? 'room_expired';
    if (code === 'plek_bezet') {
      return (
        <PlekBezet t={t} opnieuw={verbinding.opnieuw} neemOver={verbinding.neemOver} terug={terug} />
      );
    }
    // EEN OVERNAME IS GEEN AFWIJZING, en de kop hoort dat te zeggen. "Je kunt
    // er niet in" klopt niet voor iemand die er WEL in zat en zelf naar een
    // ander apparaat is gegaan.
    return (
      <Kader titel={code === 'overgenomen' ? t.overgenomenTitel : t.jeKuntErNietIn}>
        <p className="text-sm text-slate-650 mb-4">{foutmelding(code, t)}</p>
        <button
          type="button"
          onClick={terug}
          className="w-full py-2.5 rounded-xl border-2 border-slate-250 text-xs font-bold
                     text-slate-550 hover:text-gio-dark hover:border-slate-350
                     transition-colors duration-155"
        >
          {t.terug}
        </button>
      </Kader>
    );
  }

  const verbonden = verbinding.stand === 'verbonden';
  const mijnTaal = findLanguage(mijnTaalCode);
  const anderTaal = findLanguage(anderTaalCode);
  const mijnGeslacht = opPlekA ? (instellingen?.genderA ?? gender) : (instellingen?.genderB ?? gender);
  const anderGeslacht = opPlekA
    ? (instellingen?.genderB ?? 'vrouw')
    : (instellingen?.genderA ?? 'man');

  // DE ZIJBALK: links op een laptop, onder het gesprek op een telefoon. Zie
  // Kader.tsx voor hoe dat zonder tweede indeling gaat.
  const zijbalk = (
    <>
      <MeetBlok kamerId={kamer.id} meetLink={meetLink} beheer={mijnKamer} />

      <Woordenlijst kamerId={kamer.id} t={t} />

      {/*
        Alleen voor de maker van het gesprek. De vraag "ben jij dat" wordt hier
        met een briefje in localStorage beantwoord en aan de serverkant met de
        beheerpoort; zie lib/mijnKamers.ts. Je gast haalt deze regel dus niet
        eens op.
      */}
      {/* Bij alleen chat wordt er niet ingesproken: dan is er geen pauze. */}
      {mijnKamer && !alleenChat && <PauzeInstellen kamerId={kamer.id} pauzeMs={pauzeMs} />}

      {mijnKamer && (
        <Kostenbalk
          kamerId={kamer.id}
          stand={kosten.stand}
          ververs={verversKosten}
          expiresAt={verlooptOp}
        />
      )}

      {/*
        Het gespreksverslag, ook alleen voor de maker: de gegevens komen van
        achter de inlog. Een nieuw tabblad, zodat het gesprek hier gewoon
        doorloopt terwijl je het verslag opslaat.
      */}
      {mijnKamer && (
        <a
          href={verslagPad(kamer.id)}
          target="_blank"
          rel="noopener"
          className="block mt-1.5 text-[11px] font-bold text-gio-blue hover:text-gio-dark"
        >
          Gespreksverslag (PDF)
        </a>
      )}

      {/*
        Ingeklapt is de STAND VAN RUST: je hebt dit niet elk gesprek nodig.
        Daarom een dunne lijn, kleine letters en weinig lucht - net genoeg om
        te weten dat het er is. Uitgeklapt krijgt het vanzelf de ruimte.
      */}
      <details className="mt-1.5 border-t border-slate-150 pt-1.5 group">
        <summary
          className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide
                     text-slate-450 hover:text-slate-650 cursor-pointer select-none
                     transition-colors duration-155 list-none"
        >
          <span>{t.instellingenAanpassen}</span>
          <span className="font-mono group-open:hidden">+</span>
          <span className="font-mono hidden group-open:inline">−</span>
        </summary>

        {instellingen !== null && (
          <Instellingen
            t={t}
            settings={instellingen}
            opPlekA={opPlekA}
            stuur={verbinding.stuur}
          />
        )}

        <dl className="text-xs text-slate-550 space-y-1 mt-3 pt-3 border-t-2 border-slate-150
                       animate-fade-in">
          <div className="flex justify-between">
            <dt>{t.jouwPlek}</dt>
            <dd className="font-mono text-slate-450">{verbinding.slot ?? '—'}</dd>
          </div>
          <p className="pt-1 text-[11px] text-slate-450 leading-relaxed">
            {t.hoeHetWerkt}
          </p>
        </dl>
      </details>

    </>
  );

  return (
    <Kader
      titel={kamer.label === '' ? t.tolkgesprek : kamer.label}
      taalcode={mijnTaalCode}
      vol
      zijbalk={zijbalk}
    >
      {verbinding.berichten.length === 0 ? (
        <>
          {/*
            DEZELFDE knoppenrij als in de compacte kop, en dat is met opzet.
            Stond hij alleen daar, dan waren inspreken en typen niet om te
            zetten zolang er nog geen bericht was - en juist DAN heb je hem
            nodig: de keuze wordt bewaard, dus wie typen ooit uitzette kwam een
            nieuwe kamer binnen zonder tekstvak en zonder knop om het terug te
            zetten. Gemeten op 5 september 2026.
          */}
          <div className="flex justify-end mb-2">
            <Knoppenrij
              t={t}
              alleenChat={alleenChat}
              spreken={spreken}
              typen={typen}
              zetSpreken={setSpreken}
              zetTypen={setTypen}
              grootte={grootte}
              zetGrootte={zetGrootte}
            />
          </div>
          <div className="flex items-center justify-between gap-3 mb-5">
            <Zijde
              vlag={mijnTaal?.flag ?? '🏳️'}
              naam={mijnTaal?.eigenNaam ?? lang}
              label={t.jij}
              aanwezig={verbonden}
            />
            <span className="text-slate-350 text-lg select-none">⇄</span>
            <Zijde
              vlag={anderTaal?.flag ?? '🏳️'}
              naam={anderTaal?.eigenNaam ?? ''}
              label={t.deAnder}
              aanwezig={verbinding.peer.present}
            />
          </div>
        </>
      ) : (
        <KompakteKop
          t={t}
          mijnVlag={mijnTaal?.flag ?? '🏳️'}
          mijnGeslacht={mijnGeslacht}
          anderVlag={anderTaal?.flag ?? '🏳️'}
          anderGeslacht={anderGeslacht}
          anderAanwezig={verbinding.peer.present}
          alleenChat={alleenChat}
          spreken={spreken}
          typen={typen}
          zetSpreken={setSpreken}
          zetTypen={setTypen}
          grootte={grootte}
          zetGrootte={zetGrootte}
        />
      )}

      {/*
        Zodra er gepraat wordt, verdwijnen de statusblokken. Ze zijn nuttig
        terwijl je wacht en in de weg zodra het gesprek loopt - en op een
        telefoon is elke regel er een die het gesprek naar beneden duwt.
      */}
      {verbinding.berichten.length === 0 ? (
        <>
          <Stand verbinding={verbinding} peerAanwezig={verbinding.peer.present} t={t} />
          {/*
            ALLEEN BIJ EEN LEEG GESPREK, en dat is dezelfde afweging als
            hierboven. Wat er met je gesprek gebeurt, wil je weten VOOR je
            begint - niet als een regel die de rest van het gesprek ruimte kost
            op een telefoon. Wie later binnenkomt ziet het tijdstip al op het
            voorportaal staan, bij "Verloopt om".
          */}
          <BewaardTot expiresAt={verlooptOp} taalcode={mijnTaalCode} t={t} />
        </>
      ) : (
        <KorteStand verbinding={verbinding} peerAanwezig={verbinding.peer.present} t={t} />
      )}

      {/*
        De Meet-knop voor allebei, OP EEN TELEFOON. Op een breed scherm staat
        hij groot bovenaan de zijbalk (MeetBlok), en dan hoort hij hier niet
        nog een keer.
      */}
      {meetLink !== null && (
        <div className="mt-2 lg:hidden">
          <MeetKnop link={meetLink} compact />
        </div>
      )}

      {/*
        Het plafond in zicht, of bereikt: bovenaan en met de knop erin, alleen
        voor de maker. Onderaan in de kostenbalk alleen was te laat.
      */}
      {mijnKamer && kosten.stand !== null && (
        <Budgetwaarschuwing kamerId={kamer.id} stand={kosten.stand} ververs={verversKosten} />
      )}

      {/*
        HET BUDGET IS OP, EN DAT MOET JE ZIEN. Tot 23 september 2026 stuurde de
        server budget_room naar allebei en toonde de client niets: je zin ging
        gewoon niet weg. De maker krijgt hierboven de ophoogknop; de gast, en
        de maker bij de MAANDgrens (daar helpt de kamerknop niet), lezen dit.
      */}
      {(verbinding.foutcode === 'budget_month' ||
        (verbinding.foutcode === 'budget_room' && !(mijnKamer && kosten.stand !== null))) && (
        <p
          role="status"
          className="mt-2 py-2 px-3 rounded-xl border-2 border-gio-orange/40 bg-gio-orange/10
                     text-gio-orange-deep font-bold text-[11px] leading-relaxed"
        >
          {verbinding.foutcode === 'budget_month' ? t.foutBudgetMaand : t.foutBudgetKamer}
        </p>
      )}

      {/*
        Alleen als het afspelen ECHT is mislukt. Zolang de terugval het
        opvangt, hoeft er niets te staan - een waarschuwing over iets dat
        gewoon werkt, leert mensen waarschuwingen te negeren.
      */}
      {speler.fout !== null && (
        <button
          type="button"
          onClick={speler.ontgrendel}
          /*
            HIJ MOET ERUITZIEN ALS IETS WAAR JE OP DRUKT, en dat was hij niet.
            De eerste versie was een bleek oranje vlakje met kleine letters, en
            Giovanni liep er op 7 september drie keer langs terwijl hij naar een
            storing zocht. Nu: vol oranje, wit, groter, met een luidspreker
            ervoor en een zachte puls. Een melding die je niet ziet, is geen
            melding.
          */
          className="mt-2 w-full flex items-center justify-center gap-2 py-3.5 px-3
                     rounded-xl bg-gio-orange text-white font-bold text-sm
                     shadow-md animate-pulse-zacht
                     transition-transform duration-155 active:scale-[0.98]"
        >
          <span className="text-lg leading-none" aria-hidden="true">🔊</span>
          {/*
            De TEKST komt hier vandaan en niet uit de speler. Die gaf een
            Nederlandse zin terug, ook aan een Poolse gast, terwijl de vertaling
            al klaarlag. Wat de gebruiker moet doen is altijd hetzelfde: een
            keer tikken.
          */}
          <span>
            {t.geluidStaatUit} — {t.tikHier}
          </span>
        </button>
      )}

      {/*
        Spraak die niet lukte STOPT het gesprek niet: de tekst staat er wel.
        Daarom een melding die je wegklikt en geen scherm dat alles afsluit.
        Het quotum krijgt een eigen tekst, want daar helpt opnieuw proberen
        niet - dat wachten is tot morgen.
      */}
      {(verbinding.foutcode === 'speak_failed' || verbinding.foutcode === 'speak_quota') && (
        <button
          type="button"
          onClick={verbinding.wisFout}
          className="mt-2 w-full py-2 px-3 rounded-xl border-2 border-gio-orange/40
                     bg-gio-orange/10 text-gio-orange-deep font-bold text-[11px] text-left
                     transition-colors duration-155 hover:bg-gio-orange/20"
        >
          {verbinding.foutcode === 'speak_quota' ? t.foutSpraakQuota : t.foutSpraak}
        </button>
      )}

      {verbinding.peer.typing !== '' && (
        <p className="mt-3 px-3 py-2 bg-gio-orange/10 border-2 border-gio-orange/25 rounded-xl
                      text-[11px] italic text-slate-650">
          “{verbinding.peer.typing}”
        </p>
      )}

      {/*
        BEURTBEHEER: puur laten zien dat de ANDER spreekt. Nooit over
        jezelf — vandaar de tweede voorwaarde.
      */}
      {verbinding.turn !== null && verbinding.turn !== verbinding.slot && (
        <p className="mt-3 px-3 py-2 bg-gio-orange/10 border-2 border-gio-orange/25 rounded-xl
                      text-[11px] italic text-slate-650">
          {t.anderSpreekt}
        </p>
      )}

      <Gesprek
        t={t}
        kamerId={kamer.id}
        verbinding={verbinding}
        speler={speler}
        alleenChat={alleenChat}
        pauzeMs={pauzeMs}
        spreken={spreken}
        typen={typen}
        grootte={grootte}
        gelijkAfspelen={gelijkAfspelen}
        zetGelijkAfspelen={setGelijkAfspelen}
        markeerGevraagd={(id) => {
          gevraagdRef.current.add(id);
          setWachtOpAudio(id);
        }}
        wachtOpAudio={wachtOpAudio}
        meldLuistert={meldLuistert}
        meldLoslater={(los) => {
          laatMicrofoonLosRef.current = los;
        }}
      />

      {/*
        Het meetlog van het geluid, alleen met `?meet=1` in het adres. Zie
        lib/meetlog.ts. Een gast krijgt de link zonder, en ziet dit dus nooit.
      */}
      {meten() && <Meetvak />}

    </Kader>
  );
}

function Gesprek({
  t,
  kamerId,
  verbinding,
  speler,
  alleenChat,
  pauzeMs,
  spreken,
  typen,
  grootte,
  gelijkAfspelen,
  zetGelijkAfspelen,
  markeerGevraagd,
  wachtOpAudio,
  meldLuistert,
  meldLoslater,
}: {
  t: Teksten;
  /** Voor het inline woordenlijst-aanbod bij een duim omlaag op het transcript. */
  kamerId: string;
  verbinding: ReturnType<typeof useRoomSocket>;
  speler: ReturnType<typeof useAudioPlayer>;
  /** Kamer staat op alleen chat: geen microfoon, geen afspeelknoppen. */
  alleenChat: boolean;
  /** Stilte na het laatste woord voordat de zin klaar is. Van de beheerder. */
  pauzeMs: number;
  spreken: boolean;
  typen: boolean;
  grootte: number;
  gelijkAfspelen: boolean;
  zetGelijkAfspelen: (aan: boolean) => void;
  markeerGevraagd: (berichtId: string) => void;
  /** Het bericht waarvan het geluid nog onderweg is, of null. */
  wachtOpAudio: string | null;
  /** Meldt naar boven of de microfoon openstaat. Zie het beurtbeheer daar. */
  meldLuistert: (aan: boolean) => void;
  /**
   * Geeft naar boven de functie die de microfoon loslaat. De speler zit
   * daar, en die moet hem aanroepen vlak voor er iets klinkt.
   */
  meldLoslater: (los: () => void) => void;
}) {
  const [tekst, setTekst] = useState('');
  // Hoeveel seconden je krijgt om te verbeteren. 0 = meteen versturen.
  // In localStorage: wie hem eenmaal goed heeft staan, wil dat niet elk gesprek
  // opnieuw instellen.
  const [wachttijd, setWachttijd] = useState(() => {
    try {
      const ruw = localStorage.getItem('tolk.wachttijd');
      // EERST op null toetsen. Number(null) is 0, niet NaN - zonder deze regel
      // start elke verse browser op "uit" in plaats van op drie seconden.
      if (ruw === null) return 3;
      const bewaard = Number(ruw);
      return Number.isFinite(bewaard) && bewaard >= 0 && bewaard <= 10 ? bewaard : 3;
    } catch {
      return 3;
    }
  });
  /**
   * Altijd zelf versturen, zonder klok.
   *
   * Waarom dit naast de schuif bestaat: wie elke zin wil nakijken, moest het
   * vak aanraken om de teller stil te zetten. Dan is de teller geen hulp meer
   * maar iets om tegen te vechten. Nul seconden is ook geen antwoord - dan
   * verdwijnt het venster juist helemaal.
   */
  const [altijdZelf, setAltijdZelf] = useState(() => {
    try {
      return localStorage.getItem('tolk.altijdZelf') === 'ja';
    } catch {
      return false;
    }
  });
  const [bezig, setBezig] = useState(false);

  /**
   * DUIMPJES: welke kant van welk bericht al beoordeeld is.
   *
   * Puur lokaal en optimistisch - er komt geen ack terug uit het protocol.
   * De database is het echte vangnet tegen een dubbele duim (unieke index op
   * message_id + kind); dit is alleen de UI die dat weerspiegelt, zodat een
   * knop niet twee keer aan te klikken lijkt.
   */
  const [beoordeeld, setBeoordeeld] = useState<
    Record<string, Partial<Record<FeedbackKind, Thumb>>>
  >({});

  function geefDuim(berichtId: string, kind: FeedbackKind, thumb: Thumb): void {
    setBeoordeeld((oud) => ({ ...oud, [berichtId]: { ...oud[berichtId], [kind]: thumb } }));
    verbinding.stuur({ type: 'feedback', messageId: berichtId, kind, thumb });
  }

  /**
   * "Opnieuw proberen"-status per bericht.
   *
   * Vergelijkt de vertaling van VOOR het versturen met wat er nu staat: zodra
   * die verandert, is de retry aangekomen. Met een tijdslot erachteraan voor
   * het zeldzame geval dat het zwaardere model toevallig dezelfde tekst
   * teruggeeft - anders zou de knop dan voor altijd grijs blijven.
   */
  const retrySnapshot = useRef(new Map<string, string | null>());
  const [opnieuwBezig, setOpnieuwBezig] = useState<Set<string>>(new Set());

  function vraagOpnieuwAan(berichtId: string, huidigeVertaling: string | null): void {
    retrySnapshot.current.set(berichtId, huidigeVertaling);
    setOpnieuwBezig((oud) => new Set(oud).add(berichtId));
    verbinding.stuur({ type: 'retry', messageId: berichtId });
  }

  // Het scherm blijft aan zolang deze pagina open is. Dooft de iPhone, dan
  // onderbreekt iOS de geluidssessie en komt er niets meer uit tot je tikt.
  // Zie hooks/useSchermWakker.ts.
  useSchermWakker(true);

  useEffect(() => {
    if (opnieuwBezig.size === 0) return;
    setOpnieuwBezig((oud) => {
      const nieuw = new Set(oud);
      let gewijzigd = false;
      for (const id of oud) {
        const huidig = verbinding.berichten.find((b) => b.id === id);
        if (huidig === undefined || huidig.translatedText !== retrySnapshot.current.get(id)) {
          nieuw.delete(id);
          gewijzigd = true;
        }
      }
      return gewijzigd ? nieuw : oud;
    });
  }, [verbinding.berichten, opnieuwBezig]);

  useEffect(() => {
    if (opnieuwBezig.size === 0) return;
    const timer = window.setTimeout(() => setOpnieuwBezig(new Set()), 15_000);
    return () => window.clearTimeout(timer);
  }, [opnieuwBezig]);

  const onderRef = useRef<HTMLDivElement>(null);

  // In een ref: opKlaar komt uit een oudere render, en de kamer kan intussen
  // op alleen chat zijn gezet.
  const alleenChatRef = useRef(alleenChat);
  alleenChatRef.current = alleenChat;

  const spreker = useSpreken({
    // De stiltemeter moet weten wanneer de app zelf praat. Zonder dit stopt de
    // opname nooit en komt er "niets gehoord" uit een zin die er wel was.
    speeltAf: speler.speeltAf,
    pauzeMs,
    opKlaar: (resultaat) => {
      // Liep de opname nog toen de kamer op alleen chat ging, dan niet meer
      // versturen. De server zou hem toch weigeren.
      if (alleenChatRef.current) return;
      // De audio gaat naar de server; die schrijft hem uit EN vertaalt hem in
      // een aanroep. De tekst komt terug als 'concept'.
      setBezig(true);
      verbinding.stuur({
        type: 'audioUtterance',
        clientMsgId: String(Date.now()),
        audio: resultaat.audio,
        bevestigen: altijdZelf || wachttijd > 0,
      });
    },
  });

  /**
   * ALLEEN CHAT: het tekstvak altijd, de microfoon niet - ongeacht wat je
   * zelf met de schakelaars bovenin had ingesteld. Die schakelaars staan er
   * dan ook niet. Liep er nog een opname toen de kamer omging, dan blijft de
   * knop staan tot je hem stopt: een opname zonder stopknop is erger.
   */
  const microZichtbaar = spreken && (!alleenChat || spreker.luistert);
  const typenZichtbaar = typen || alleenChat;

  // Zie laatMicrofoonLosRef in RoomPage. In een effect, niet tijdens het
  // renderen: React mag een render afbreken en dan heb je een ref gezet voor
  // een render die nooit geteld heeft.
  useEffect(() => {
    meldLoslater(spreker.laatMicrofoonLos);
  }, [spreker.laatMicrofoonLos, meldLoslater]);

  /**
   * De stand van de microfoon naar boven melden, EN naar de server.
   *
   * meldLuistert regelt het CLIENT-side deel: het afspelen wordt
   * tegengehouden zolang jij aan het woord bent. turnClaim/turnRelease
   * stuurt dat alleen door naar de server, zodat de ANDERE deelnemer het op
   * zijn scherm ziet — puur zichtbaar maken, geen afdwinging.
   *
   * Bij het opruimen gaat de melding op false: wie het gesprek verlaat
   * terwijl de opname loopt, zou anders een rij fragmenten achterlaten die
   * nooit meer gespeeld wordt, en de server zou denken dat je nog spreekt.
   */
  useEffect(() => {
    meldLuistert(spreker.luistert);
    verbinding.stuur({ type: spreker.luistert ? 'turnClaim' : 'turnRelease' });
    return () => {
      meldLuistert(false);
      verbinding.stuur({ type: 'turnRelease' });
    };
  }, [spreker.luistert, meldLuistert, verbinding.stuur]);

  /**
   * "Uitschrijven…" moet ALTIJD een einde hebben.
   *
   * Er waren drie manieren om er niet uit te komen, en Giovanni liep tegen de
   * eerste aan: een te groot bericht verbrak de verbinding, de client verbond
   * opnieuw, en de knop bleef eeuwig op "Uitschrijven..." staan.
   *
   * Nu stopt hij bij het CONCEPT, bij een nieuw BERICHT, bij een FOUT, bij
   * verbindingsverlies, en anders na dertig seconden. Een knop die vastloopt
   * is erger dan een die te vroeg loslaat: dan weet je tenminste dat je het
   * opnieuw moet proberen.
   */
  useEffect(() => {
    if (verbinding.concept !== null) setBezig(false);
  }, [verbinding.concept]);
  useEffect(() => {
    if (verbinding.berichten.length > 0) setBezig(false);
  }, [verbinding.berichten.length]);
  useEffect(() => {
    if (verbinding.foutcode !== null) setBezig(false);
  }, [verbinding.foutcode]);
  useEffect(() => {
    if (verbinding.stand !== 'verbonden') setBezig(false);
  }, [verbinding.stand]);
  useEffect(() => {
    if (!bezig) return;
    const timer = window.setTimeout(() => setBezig(false), 30_000);
    return () => window.clearTimeout(timer);
  }, [bezig]);

  // Ook op de laatste VERTALING letten, niet alleen op het aantal berichten:
  // een bubbel groeit als de vertaling binnenkomt, en dan sta je weer niet
  // onderaan.
  const laatsteVertaling = verbinding.berichten.at(-1)?.translatedText ?? '';
  useEffect(() => {
    onderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [verbinding.berichten.length, laatsteVertaling, grootte]);

  const kanVersturen = verbinding.stand === 'verbonden' && tekst.trim() !== '';
  // Laatste tien seconden: dan is het tijd om af te ronden.
  const bijnaVol = spreker.verstreken >= spreker.maxSeconden - 10;

  function verstuur(): void {
    if (!kanVersturen) return;
    verbinding.stuur({ type: 'utterance', clientMsgId: String(Date.now()), text: tekst.trim() });
    setTekst('');
  }

  return (
    /*
      GEEN `min-h-0` HIER, EN DAT IS EEN REPARATIE. Dit vak was het enige in de
      kaart dat kon krimpen, dus het ving in zijn eentje elk tekort op. Met de
      kostenbalk en het meetvak erbij bleef er 6 px over voor een blok van 194
      px, en de spreekknop, de schuif en het tekstvak liepen dwars door de
      kosten heen. Gemeten op een scherm van 375x812, 13 september 2026.

      Zonder `min-h-0` geldt hier de standaard van een flex-item: nooit kleiner
      dan zijn eigen inhoud. De inhoud is de berichtenlijst (die wel mag
      krimpen, zie hieronder) plus de invoer, dus de bodem is precies zo hoog
      als de invoer op dat moment nodig heeft. ZELFVERSTELLEND en geen vast
      getal: zet je de tekst groter, dan groeit de bodem mee.

      Past het geheel dan niet meer, dan scrolt de kaart - zie Kader.tsx.
    */
    <div className="flex-1 flex flex-col mt-2">
      {/*
        De berichten krijgen ALLE ruimte die overblijft. Klap je iets in, dan
        wordt dit vak groter - dat was het hele punt van het inklappen. Met een
        vaste hoogte werd de kaart alleen korter.

        `min-h-[4rem]` in plaats van `min-h-0`: allebei laten dit vak krimpen
        onder zijn inhoud, maar 0 betekent dat er bij een vol scherm GEEN enkel
        bericht meer zichtbaar is. Vier regelhoogtes houden er altijd een in
        beeld.
      */}
      <div className="flex-1 min-h-[4rem] overflow-y-auto mb-2 pr-1">
        {/*
          De berichten hangen ONDERAAN, niet bovenaan. Het nieuwste bericht
          staat dan vlak boven de invoer, waar je toch al kijkt - in plaats van
          bovenin met een gat eronder. Bij veel berichten scrollt het gewoon.
        */}
        <div className="min-h-full flex flex-col justify-end space-y-2">
          {verbinding.berichten.map((b) => {
            const vanMij = b.senderSlot === verbinding.slot;
            // Een duim kan pas als er echt iets staat om te beoordelen -
            // niet terwijl het nog vertaalt of als het mislukt is, want dan
            // is er ook nog geen model bekend.
            const teBeoordelen = b.status === 'translated';
            return (
              <div key={b.id}>
                <Bericht
                  bericht={b}
                  mijnSlot={verbinding.slot ?? 'a'}
                  klinkt={speler.klinkt === b.id}
                  // Ook uit terwijl JIJ aan het woord bent. Een fragment starten
                  // met de microfoon open zet de app in je eigen opname.
                  // Ook op slot zolang er geluid ONDERWEG is. Anders start
                  // een tweede druk een tweede zin die er dwars doorheen komt.
                  speeltAf={speler.speeltAf || spreker.luistert || wachtOpAudio !== null}
                  wacht={wachtOpAudio === b.id}
                  grootte={grootte}
                  t={t}
                  speelOpnieuw={
                    vanMij || alleenChat
                      ? undefined
                      : () => {
                          markeerGevraagd(b.id);
                          verbinding.stuur({ type: 'requestAudio', messageId: b.id });
                        }
                  }
                  stopAfspelen={() => speler.stop()}
                  speelBron={
                    !vanMij && !alleenChat && b.heeftBronAudio === true
                      ? () => verbinding.stuur({ type: 'requestAudio', messageId: b.id, bron: true })
                      : undefined
                  }
                  duimTranscript={
                    vanMij && b.ingesproken === true && teBeoordelen
                      ? (thumb) => geefDuim(b.id, 'transcript', thumb)
                      : undefined
                  }
                  duimVertaling={
                    !vanMij && teBeoordelen ? (thumb) => geefDuim(b.id, 'translation', thumb) : undefined
                  }
                  gegevenTranscript={beoordeeld[b.id]?.transcript ?? null}
                  gegevenVertaling={beoordeeld[b.id]?.translation ?? null}
                  opnieuwProberen={
                    !vanMij && teBeoordelen
                      ? () => vraagOpnieuwAan(b.id, b.translatedText)
                      : undefined
                  }
                  bezigMetOpnieuwProberen={opnieuwBezig.has(b.id)}
                />
                {vanMij && beoordeeld[b.id]?.transcript === 'down' && (
                  <div className="max-w-[92%] ml-auto mt-1">
                    <p className="text-[11px] text-slate-450 mb-1">
                      {t.duimTranscriptWoordenlijstUitnodiging}
                    </p>
                    <Woordenlijst kamerId={kamerId} t={t} voorstel={b.originalText} starttOpen />
                  </div>
                )}
              </div>
            );
          })}
        <div ref={onderRef} />
        </div>
      </div>

      <div className="shrink-0">
      {spreker.fout !== null && (
        <p className="mb-2 text-xs text-gio-danger bg-gio-danger/5 border-2 border-gio-danger/20
                      rounded-xl px-3 py-2">
          {spreekMelding(spreker.fout, t)}
        </p>
      )}

      {verbinding.concept !== null ? (
        <Bevestiging
          tekst={verbinding.concept.bron}
          seconden={wachttijd}
          handmatig={altijdZelf}
          t={t}
          opVerstuur={(zin) => {
            verbinding.stuur({
              type: 'bevestig',
              clientMsgId: verbinding.concept?.clientMsgId ?? '',
              text: zin,
            });
            verbinding.wisConcept();
          }}
          opAnnuleer={() => {
            verbinding.stuur({
              type: 'verwerpConcept',
              clientMsgId: verbinding.concept?.clientMsgId ?? '',
            });
            verbinding.wisConcept();
          }}
        />
      ) : (
        <div className={`items-center gap-2 mb-2 ${microZichtbaar ? 'flex' : 'hidden'}`}>
          <button
            type="button"
            onClick={() => (spreker.luistert ? spreker.stop() : spreker.start())}
            // NIET beginnen terwijl de app praat. Eén geluidsbron tegelijk:
            // anders neem je de vertaling van de ander op als je eigen zin.
            // Stoppen mag altijd - een knop die niet loslaat is erger.
            disabled={
              verbinding.stand !== 'verbonden' ||
              !spreker.kanOpnemen ||
              bezig ||
              (!spreker.luistert && speler.speeltAf)
            }
            className={`relative flex-1 py-3 rounded-xl font-bold text-sm overflow-hidden
                        transition-colors duration-155
                        disabled:opacity-40 disabled:cursor-not-allowed ${
                          spreker.luistert
                            ? bijnaVol
                              ? 'bg-gio-danger text-white'
                              : 'bg-gio-orange text-white'
                            : 'bg-gio-blue hover:bg-gio-dark text-white'
                        }`}
          >
            {/*
              De balk loopt mee met de opname. Er is een harde grens van een
              minuut, en die wil je zien AANKOMEN in plaats van er tegenaan te
              lopen midden in een zin.
            */}
            {spreker.luistert && (
              <span
                className="absolute inset-y-0 left-0 bg-white/25 transition-[width] duration-500
                           ease-linear pointer-events-none"
                style={{ width: `${Math.min(100, (spreker.verstreken / spreker.maxSeconden) * 100)}%` }}
              />
            )}
            <span className="relative">
              {bezig ? t.uitschrijven : spreker.luistert ? t.luisteren : t.spreekIn}
              {spreker.luistert && (
                <span className="ml-2 font-mono tabular-nums">{klok(spreker.verstreken)}</span>
              )}
            </span>
          </button>
        </div>
      )}

      {/*
        Hoeveel tijd je krijgt om te verbeteren. Op nul gaat de zin meteen weg
        en verschijnt er geen venster - dan praat je in een doorlopend gesprek
        zonder onderbreking.
      */}
      {verbinding.concept === null && microZichtbaar && !altijdZelf && (
        <label className="flex items-center gap-2 mb-2 text-[11px] text-slate-450 select-none">
          <span className="shrink-0">{t.tijdOmTeVerbeteren}</span>
          <input
            type="range"
            min={0}
            max={10}
            step={1}
            value={wachttijd}
            onChange={(e) => {
              const n = Number(e.target.value);
              setWachttijd(n);
              try {
                localStorage.setItem('tolk.wachttijd', String(n));
              } catch {
                // Geblokkeerde opslag. Dan geldt hij alleen dit gesprek.
              }
            }}
            className="flex-1 min-w-0 accent-gio-orange cursor-pointer"
          />
          <span className="font-mono text-slate-650 w-12 text-right shrink-0">
            {wachttijd === 0 ? t.uit : `${wachttijd} ${t.sec}`}
          </span>
        </label>
      )}

      {/*
        De twee vinkjes NAAST elkaar, met wrap. Onder elkaar kostten ze twee
        regels aan hoogte die het gesprek kwijt was; naast elkaar passen ze op
        een. Wordt het te smal - een lange taal, een smalle telefoon - dan
        springt de tweede vanzelf naar de volgende regel.
      */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
        {verbinding.concept === null && microZichtbaar && (
          <label className="flex items-center gap-2 text-[11px] text-slate-450
                            cursor-pointer select-none">
            <input
              type="checkbox"
              checked={altijdZelf}
              onChange={(e) => {
                setAltijdZelf(e.target.checked);
                try {
                  localStorage.setItem('tolk.altijdZelf', e.target.checked ? 'ja' : 'nee');
                } catch {
                  // Geblokkeerde opslag. Dan geldt hij alleen dit gesprek.
                }
              }}
              className="w-3.5 h-3.5 accent-gio-orange cursor-pointer shrink-0"
            />
            <span>{t.altijdZelfVersturen}</span>
          </label>
        )}

        {/* Bij alleen chat klinkt er niets, dus ook niets om meteen af te spelen. */}
        {!alleenChat && (
          <label className="flex items-center gap-2 text-[11px] text-slate-450
                            cursor-pointer select-none">
            <input
              type="checkbox"
              checked={gelijkAfspelen}
              onChange={(e) => {
                zetGelijkAfspelen(e.target.checked);
                try {
                  localStorage.setItem('tolk.gelijkAfspelen', e.target.checked ? 'ja' : 'nee');
                } catch {
                  // Geblokkeerde opslag. Dan geldt hij alleen dit gesprek.
                }
              }}
              className="w-3.5 h-3.5 accent-gio-orange cursor-pointer shrink-0"
            />
            <span>{t.vertalingMeteenAfspelen}</span>
          </label>
        )}
      </div>

      {spreker.luistert && (
        <div className="mb-2 flex items-end justify-center gap-1 h-8" aria-hidden="true">
          {/* Balkjes die met je stem meebewegen. Zonder herkenning is dit het
              enige teken dat de microfoon je hoort. */}
          {[0.4, 0.7, 1, 0.8, 0.5, 0.9, 0.6].map((f, i) => (
            <span
              key={i}
              className="w-1.5 rounded-full bg-gio-orange transition-all duration-100"
              style={{ height: `${Math.max(12, spreker.niveau * f * 100)}%` }}
            />
          ))}
        </div>
      )}

      {!spreker.kanOpnemen && !alleenChat && (
        <p className="mb-2 text-[11px] text-slate-450 leading-relaxed">
          {t.geenOpnameMogelijk}
        </p>
      )}

      <div className={`gap-2 ${typenZichtbaar ? 'flex' : 'hidden'}`}>
        <textarea
          value={tekst}
          onChange={(e) => setTekst(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              verstuur();
            }
          }}
          rows={2}
          placeholder={t.typHierJeBericht}
          disabled={verbinding.stand !== 'verbonden'}
          className="flex-1 p-2.5 bg-slate-50 border-2 border-slate-250 rounded-xl text-sm
                     text-slate-800 placeholder-slate-405 resize-none
                     focus:outline-none focus:border-gio-blue focus:bg-white
                     transition-colors duration-155 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={verstuur}
          disabled={!kanVersturen}
          className="px-4 rounded-xl bg-gio-blue hover:bg-gio-dark text-white font-bold text-xs
                     transition-colors duration-155 disabled:opacity-30
                     disabled:cursor-not-allowed"
        >
          {t.stuur}
        </button>
      </div>
      </div>
    </div>
  );
}

function Stand({
  verbinding,
  peerAanwezig,
  t,
}: {
  verbinding: ReturnType<typeof useRoomSocket>;
  peerAanwezig: boolean;
  t: Teksten;
}) {
  if (verbinding.stand === 'opnieuw_verbinden') {
    return (
      <Melding kleur="oranje">
        {t.verbindingKwijt}{' '}
        {verbinding.poging > 1 && <span className="font-mono">{verbinding.poging}</span>}
      </Melding>
    );
  }
  if (verbinding.stand !== 'verbonden') {
    return <Melding kleur="grijs">{t.verbinden}</Melding>;
  }
  return peerAanwezig ? (
    <Melding kleur="groen">{t.allebeiInDeKamer}</Melding>
  ) : (
    <Melding kleur="grijs">{t.wachtenOpAnder}</Melding>
  );
}

/** Seconden als 0:07. Twee cijfers, zodat de knop niet gaat wiebelen. */
function klok(seconden: number): string {
  const m = Math.floor(seconden / 60);
  const s = seconden % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Een bewaarde aan-uitstand. Ontbreekt hij, dan geldt de standaard. */
function leesStand(sleutel: string, standaard: boolean): boolean {
  try {
    const ruw = localStorage.getItem(sleutel);
    return ruw === null ? standaard : ruw === 'ja';
  } catch {
    return standaard;
  }
}

function bewaarStand(sleutel: string, aan: boolean): void {
  try {
    localStorage.setItem(sleutel, aan ? 'ja' : 'nee');
  } catch {
    // Geblokkeerde opslag. Dan geldt het alleen dit gesprek.
  }
}

/**
 * De kop zodra er gepraat wordt: twee vlaggen met een teken erbij, en twee
 * knopjes om inspreken en typen te verbergen.
 *
 * Alles wat je niet gebruikt kost hier ruimte die het gesprek nodig heeft. Op
 * een telefoon scheelt dit een kwart van het scherm.
 */
function KompakteKop({
  t,
  mijnVlag,
  mijnGeslacht,
  anderVlag,
  anderGeslacht,
  anderAanwezig,
  alleenChat,
  spreken,
  typen,
  zetSpreken,
  zetTypen,
  grootte,
  zetGrootte,
}: {
  t: Teksten;
  mijnVlag: string;
  mijnGeslacht: Gender;
  anderVlag: string;
  anderGeslacht: Gender;
  anderAanwezig: boolean;
  alleenChat: boolean;
  spreken: boolean;
  typen: boolean;
  zetSpreken: (aan: boolean) => void;
  zetTypen: (aan: boolean) => void;
  grootte: number;
  zetGrootte: (n: number) => void;
}) {
  const teken = (g: Gender): string => (g === 'man' ? '♂' : '♀');

  return (
    <div className="flex items-center justify-between gap-2 mb-2">
      <span className="flex items-center gap-1.5 text-sm">
        <span>{mijnVlag}</span>
        <span className="text-slate-450 text-xs">{teken(mijnGeslacht)}</span>
        <span className="text-slate-350 mx-0.5">⇄</span>
        <span className={anderAanwezig ? '' : 'opacity-40'}>{anderVlag}</span>
        <span className="text-slate-450 text-xs">{teken(anderGeslacht)}</span>
      </span>

      <Knoppenrij
        t={t}
        alleenChat={alleenChat}
        spreken={spreken}
        typen={typen}
        zetSpreken={zetSpreken}
        zetTypen={zetTypen}
        grootte={grootte}
        zetGrootte={zetGrootte}
      />
    </div>
  );
}

/**
 * Tekstgrootte, en de twee schakelaars voor inspreken en typen.
 *
 * EEN component, gebruikt in ALLEBEI de koppen. Stond hij alleen in de
 * compacte kop, dan waren deze knoppen onbereikbaar zolang het gesprek nog
 * leeg was - precies het moment waarop je ze het hardst nodig hebt.
 */
function Knoppenrij({
  t,
  alleenChat,
  spreken,
  typen,
  zetSpreken,
  zetTypen,
  grootte,
  zetGrootte,
}: {
  t: Teksten;
  /** Bij alleen chat valt er niets te kiezen: dan geen inspreek- en typschakelaar. */
  alleenChat: boolean;
  spreken: boolean;
  typen: boolean;
  zetSpreken: (aan: boolean) => void;
  zetTypen: (aan: boolean) => void;
  grootte: number;
  zetGrootte: (n: number) => void;
}) {
  return (
    <span className="flex items-center gap-1">
      {/* Tekstgrootte. Klein en dicht bij de tekst waar het over gaat. */}
      <button
        type="button"
        onClick={() => zetGrootte(grootte - 1)}
        disabled={grootte <= 0}
        title={t.kleinereTekst}
        className="w-7 h-7 rounded-lg border-2 border-slate-250 bg-slate-50 text-slate-550
                   text-sm font-bold flex items-center justify-center
                   hover:border-slate-350 transition-colors duration-155
                   disabled:opacity-30"
      >
        −
      </button>
      <button
        type="button"
        onClick={() => zetGrootte(grootte + 1)}
        disabled={grootte >= 4}
        title={t.grotereTekst}
        className="w-7 h-7 rounded-lg border-2 border-slate-250 bg-slate-50 text-slate-550
                   text-sm font-bold flex items-center justify-center mr-1
                   hover:border-slate-350 transition-colors duration-155
                   disabled:opacity-30"
      >
        +
      </button>

      {!alleenChat && (
        <>
          <Schakelaar
            aan={spreken}
            titel={spreken ? t.insprekenVerbergen : t.insprekenTonen}
            // Nooit allebei uit: dan kun je niets meer zeggen en is er geen weg
            // terug binnen het gesprek.
            uitschakelbaar={typen}
            opKlik={() => {
              zetSpreken(!spreken);
              bewaarStand('tolk.spreken', !spreken);
            }}
          >
            🎤
          </Schakelaar>
          <Schakelaar
            aan={typen}
            titel={typen ? t.toetsenbordVerbergen : t.toetsenbordTonen}
            uitschakelbaar={spreken}
            opKlik={() => {
              zetTypen(!typen);
              bewaarStand('tolk.typen', !typen);
            }}
          >
            ⌨
          </Schakelaar>
        </>
      )}
    </span>
  );
}

function Schakelaar({
  aan,
  titel,
  uitschakelbaar,
  opKlik,
  children,
}: {
  aan: boolean;
  titel: string;
  uitschakelbaar: boolean;
  opKlik: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={titel}
      aria-pressed={aan}
      disabled={aan && !uitschakelbaar}
      onClick={opKlik}
      className={`w-7 h-7 rounded-lg border-2 text-xs flex items-center justify-center
                  transition-colors duration-155 disabled:opacity-30 ${
                    aan
                      ? 'border-gio-blue/40 bg-gio-blue/10'
                      : 'border-slate-250 bg-slate-50 opacity-50'
                  }`}
    >
      {children}
    </button>
  );
}

/**
 * Tot wanneer dit gesprek bewaard blijft.
 *
 * WAAROM DIT ER STAAT. Tot 7 september 2026 werd een gesprek nergens bewaard.
 * Sloot je je tabblad, dan was het weg - zonder een woord. Sinds die dag blijft
 * het staan tot de kamer verloopt, en dat is óók iets om te melden: er staat
 * tekst van je op de server, en op een dag staat hij er niet meer. Van een
 * verrassing een eigenschap maken.
 *
 * KLEIN EN GRIJS, MET OPZET. Dit is geen waarschuwing en geen knop. Het hoort
 * bij de regel "Verbonden" erboven: informatie voor wie ernaar zoekt, niet iets
 * dat om aandacht vraagt terwijl je aan het praten bent.
 *
 * DE TIJDNOTATIE VOLGT DE GEKOZEN TAAL, niet die van het toestel. Wie de app in
 * het Duits zet, hoort ook een Duitse klok te zien.
 */
function BewaardTot({
  expiresAt,
  taalcode,
  t,
}: {
  expiresAt: number;
  taalcode: string;
  t: Teksten;
}) {
  // Een vaste kamer heeft geen einddatum om te tonen, wel een bewaartermijn.
  if (isVasteKamer(expiresAt)) {
    return <p className="mt-1 text-[11px] text-slate-450">{t.bewaardVast}</p>;
  }
  const eind = new Date(expiresAt);
  // Verloopt het vandaag nog, dan is de datum ruis: dan zegt een tijdstip
  // alles. Loopt het door tot morgen of later, dan zegt "17:45" juist te
  // weinig - dan hoort de dag erbij.
  const vandaag = new Date().toDateString() === eind.toDateString();
  let wanneer: string;
  try {
    wanneer = vandaag
      ? eind.toLocaleTimeString(taalcode, { hour: '2-digit', minute: '2-digit' })
      : eind.toLocaleString(taalcode, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        });
  } catch {
    // Een taalcode die de browser niet kent, gooit een RangeError. Dan liever
    // de eigen notatie van het toestel dan een leeg scherm.
    wanneer = eind.toLocaleString();
  }

  return (
    <p className="mt-1 text-[11px] text-slate-450">{t.bewaardTot.replace('{tijd}', wanneer)}</p>
  );
}

/** Een dunne regel in plaats van een blok. Voor als het gesprek al loopt. */
function KorteStand({
  verbinding,
  peerAanwezig,
  t,
}: {
  verbinding: ReturnType<typeof useRoomSocket>;
  peerAanwezig: boolean;
  t: Teksten;
}) {
  const opnieuw = verbinding.stand === 'opnieuw_verbinden';
  const goed = verbinding.stand === 'verbonden' && peerAanwezig;

  return (
    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-450">
      <span
        className={`w-1.5 h-1.5 rounded-full shrink-0 ${
          opnieuw ? 'bg-gio-orange animate-pulse' : goed ? 'bg-emerald-500' : 'bg-slate-350'
        }`}
      />
      {opnieuw
        ? t.verbindingKwijt
        : goed
          ? t.verbonden
          : verbinding.stand === 'verbonden'
            ? t.anderIsWeg
            : t.verbinden}
    </p>
  );
}

function Melding({ kleur, children }: { kleur: 'groen' | 'oranje' | 'grijs'; children: React.ReactNode }) {
  const stijl = {
    groen: 'bg-emerald-50 border-emerald-250 text-emerald-650',
    oranje: 'bg-gio-orange/10 border-gio-orange/30 text-gio-orange-deep',
    grijs: 'bg-slate-50 border-slate-250 text-slate-550',
  }[kleur];
  return (
    <p className={`text-xs font-semibold border-2 rounded-xl px-3 py-2.5 ${stijl}`}>{children}</p>
  );
}

function Zijde({
  vlag,
  naam,
  label,
  aanwezig,
}: {
  vlag: string;
  naam: string;
  label: string;
  aanwezig: boolean;
}) {
  return (
    <div
      className={`flex-1 text-center border-2 rounded-xl py-3 transition-colors duration-250 ${
        aanwezig ? 'border-emerald-250 bg-emerald-50/40' : 'border-slate-250 bg-slate-50'
      }`}
    >
      <div className={`text-2xl leading-none mb-1 ${aanwezig ? '' : 'opacity-40'}`}>{vlag}</div>
      <div className="text-xs font-bold text-gio-dark">{naam}</div>
      <div className="flex items-center justify-center gap-1 mt-1">
        <span
          className={`w-1.5 h-1.5 rounded-full ${aanwezig ? 'bg-emerald-500' : 'bg-slate-350'}`}
        />
        <span className="text-[10px] text-slate-450">{label}</span>
      </div>
    </div>
  );
}
